import React, { useEffect, useState } from 'react';
import { Save, AlertCircle, TrendingUp } from 'lucide-react';
import api from '../lib/api';
import HelpCallout from './HelpCallout';

const FEATURE_LABELS = {
  town_inspiration: 'AI town narratives',
  plan_hook: 'Plan-my-day AI hooks',
  weekend_digest: 'Weekly AI digest',
  venue_copy: 'Admin AI copy assist',
  unknown: 'Other AI use',
};

const FIXED_FIELDS = [
  { key: 'emergent_subscription_gbp_monthly', label: 'Emergent platform subscription (£/month)', placeholder: '20.00' },
  { key: 'mongodb_gbp_monthly', label: 'MongoDB hosting (£/month)', placeholder: '0.00 (free tier)' },
  { key: 'resend_paid_gbp_monthly', label: 'Resend paid tier (£/month)', placeholder: '0.00 (free under 3k/mo)' },
  { key: 'domain_gbp_annual', label: 'Domain registration (£/year)', placeholder: '12.99' },
  { key: 'daily_ai_cap_gbp', label: 'Daily AI spend cap — alert when crossed (£)', placeholder: '2.00 (0 = disabled)' },
];

export default function AdminCosts() {
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  function load() {
    api.get('/admin/costs/summary?weeks=8').then((r) => {
      setSummary(r.data);
      setSettings(r.data.settings);
    }).catch((err) => {
      if (err?.response?.status === 403) setForbidden(true);
    });
  }
  useEffect(load, []);

  async function saveSettings() {
    setSaving(true);
    try {
      const patch = {};
      FIXED_FIELDS.forEach((f) => { patch[f.key] = parseFloat(settings[f.key]) || 0; });
      patch.notes = settings.notes || '';
      const r = await api.put('/admin/costs/settings', patch);
      setSettings(r.data);
      setSavedAt(new Date().toLocaleTimeString('en-GB'));
      load();
    } finally {
      setSaving(false);
    }
  }

  if (forbidden) {
    return (
      <div className="max-w-2xl bg-white border border-charcoal/10 rounded-3xl p-10 text-center" data-testid="costs-owner-only">
        <AlertCircle size={28} className="mx-auto text-heritage mb-4"/>
        <h1 className="font-display text-3xl mb-3">Owner only</h1>
        <p className="text-muted">Running-cost figures include AI spend and email volume that the workspace owner is responsible for. Ask the owner to share a screenshot if you need the numbers.</p>
      </div>
    );
  }

  if (!summary) return <div className="p-12 text-muted">Loading cost data…</div>;

  const maxWeek = Math.max(0.01, ...summary.weeks.map((w) => (w.total_gbp || 0) + (summary.fixed_weekly_gbp || 0)));
  const currentWeek = summary.weeks[summary.weeks.length - 1] || {};
  const featureBreakdown = Object.entries(currentWeek.by_feature || {}).sort((a, b) => b[1] - a[1]);

  return (
    <div className="max-w-6xl">
      <div className="label text-heritage mb-2">Running costs</div>
      <h1 className="font-display text-4xl mb-2">What the site is costing.</h1>
      <p className="text-muted mb-8">Estimated weekly spend across AI, emails and platform fees. Variable usage is logged automatically; fixed fees are managed below.</p>

      <HelpCallout
        title="Estimates only — the source of truth is each provider's billing dashboard."
        description="LLM token costs are estimated using a 4-chars-per-token heuristic and published rates. Resend tracks every send; the first 3,000/month are free."
        appearsAt={[
          { label: 'Dashboard tile', where: 'shows the headline "This week (£)" figure (variable + apportioned fixed)' },
          { label: 'Settings panel below', where: 'where you plug in the monthly/annual fixed fees the platform can\'t see' },
        ]}
      />

      {/* Headline figures */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <div className="bg-white border border-charcoal/10 rounded-2xl p-6" data-testid="cost-this-week">
          <div className="label text-charcoal/50">This week (total)</div>
          <div className="font-display text-5xl mt-3">£{(summary.this_week_total_gbp || 0).toFixed(2)}</div>
          <div className="text-sm text-muted mt-2">
            Variable £{(summary.this_week_variable_gbp || 0).toFixed(2)} · Fixed £{(summary.fixed_weekly_gbp || 0).toFixed(2)}
          </div>
        </div>
        <div className="bg-white border border-charcoal/10 rounded-2xl p-6">
          <div className="label text-charcoal/50">Resend usage this month</div>
          <div className="font-display text-5xl mt-3" data-testid="cost-resend-pct">{summary.resend.pct_used}%</div>
          <div className="text-sm text-muted mt-2">{summary.resend.emails_this_month} of {summary.resend.free_quota} free emails</div>
          {summary.resend.over_quota > 0 && (
            <div className="text-xs text-heritage mt-2 inline-flex items-center gap-1">
              <AlertCircle size={11}/> £{summary.resend.overage_gbp.toFixed(2)} overage so far
            </div>
          )}
        </div>
        <div className="bg-white border border-charcoal/10 rounded-2xl p-6">
          <div className="label text-charcoal/50">Annualised</div>
          <div className="font-display text-5xl mt-3">£{((summary.this_week_total_gbp || 0) * 52).toFixed(0)}</div>
          <div className="text-sm text-muted mt-2">if this week&apos;s pace holds for a year</div>
        </div>
      </div>

      {/* Daily AI spend vs configured cap */}
      {summary.daily_cap_gbp > 0 && (() => {
        const pct = Math.min(100, Math.round((summary.today_ai_gbp / summary.daily_cap_gbp) * 100));
        const breached = summary.today_ai_gbp >= summary.daily_cap_gbp;
        return (
          <div className={`mb-10 rounded-2xl p-6 border ${breached ? 'bg-heritage/10 border-heritage' : 'bg-white border-charcoal/10'}`} data-testid="daily-cap-meter">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div>
                <div className="label text-charcoal/50">Today&apos;s AI spend</div>
                <div className="font-display text-3xl mt-1">£{summary.today_ai_gbp.toFixed(4)} <span className="text-base text-muted">of £{summary.daily_cap_gbp.toFixed(2)} cap</span></div>
              </div>
              {breached && (
                <div className="text-sm text-heritage font-semibold inline-flex items-center gap-1.5">
                  <AlertCircle size={14}/> Cap breached — alert fired
                </div>
              )}
            </div>
            <div className="bg-admin h-3 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${breached ? 'bg-heritage' : pct > 75 ? 'bg-mustard' : 'bg-forest'}`} style={{ width: `${pct}%` }}/>
            </div>
          </div>
        );
      })()}

      {/* 8-week sparkline */}
      <div className="bg-white border border-charcoal/10 rounded-2xl p-6 mb-10">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label text-charcoal/50">Last 8 weeks</div>
            <h2 className="font-display text-2xl mt-1">Weekly trend</h2>
          </div>
          <TrendingUp size={20} className="text-muted"/>
        </div>
        <div className="flex items-end gap-2 h-32" data-testid="cost-trend">
          {summary.weeks.map((w, i) => {
            const total = (w.total_gbp || 0) + summary.fixed_weekly_gbp;
            const heightPct = Math.max(2, Math.round((total / maxWeek) * 100));
            return (
              <div key={i} className="flex-1 flex flex-col justify-end items-center gap-1.5">
                <div className="text-xs font-semibold text-charcoal/60">£{total.toFixed(2)}</div>
                <div className={`w-full rounded-t-md ${i === summary.weeks.length - 1 ? 'bg-heritage' : 'bg-charcoal/15'}`} style={{ height: `${heightPct}%` }} title={`Week of ${w.week_start}`}/>
                <div className="text-[10px] text-charcoal/50">{w.week_start.slice(5)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* What's generating the cost this week */}
      <div className="bg-white border border-charcoal/10 rounded-2xl p-6 mb-10">
        <div className="label text-charcoal/50 mb-1">This week&apos;s variable spend</div>
        <h2 className="font-display text-2xl mb-5">What&apos;s driving the cost</h2>
        {featureBreakdown.length === 0 && currentWeek.emails_sent === 0 ? (
          <div className="text-muted text-sm py-6">No variable spend yet this week.</div>
        ) : (
          <ul className="space-y-3" data-testid="cost-breakdown">
            {featureBreakdown.map(([feature, gbp]) => {
              const pct = Math.round((gbp / Math.max(0.001, currentWeek.total_gbp)) * 100);
              return (
                <li key={feature} className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{FEATURE_LABELS[feature] || feature}</div>
                    <div className="bg-admin h-2 rounded-full overflow-hidden mt-1">
                      <div className="bg-forest h-full rounded-full" style={{ width: `${pct}%` }}/>
                    </div>
                  </div>
                  <div className="text-sm tabular-nums font-semibold text-right w-20">£{gbp.toFixed(4)}</div>
                  <div className="text-xs text-muted w-10 text-right">{pct}%</div>
                </li>
              );
            })}
            {currentWeek.emails_sent > 0 && (
              <li className="flex items-center gap-4 pt-3 border-t border-charcoal/10">
                <div className="flex-1 text-sm font-semibold">Resend emails sent</div>
                <div className="text-sm tabular-nums w-20 text-right">{currentWeek.emails_sent}</div>
                <div className="text-xs text-muted w-10 text-right">free</div>
              </li>
            )}
          </ul>
        )}
      </div>

      {/* Manual fixed-cost settings */}
      <div className="bg-white border border-charcoal/10 rounded-2xl p-6">
        <div className="label text-charcoal/50 mb-1">Fixed monthly / annual fees</div>
        <h2 className="font-display text-2xl mb-2">Costs the site can&apos;t auto-detect</h2>
        <p className="text-sm text-muted mb-6">Plug in what you pay each month or year. We annualise into a weekly figure for the dashboard tile.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIXED_FIELDS.map((f) => (
            <label key={f.key} className="block">
              <div className="label mb-1.5">{f.label}</div>
              <input
                type="number"
                step="0.01"
                value={settings?.[f.key] ?? ''}
                onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })}
                placeholder={f.placeholder}
                className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"
                data-testid={`cost-input-${f.key}`}
              />
            </label>
          ))}
        </div>
        <label className="block mt-4">
          <div className="label mb-1.5">Notes (anything else worth remembering)</div>
          <textarea
            rows={2}
            value={settings?.notes || ''}
            onChange={(e) => setSettings({ ...settings, notes: e.target.value })}
            className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"
            data-testid="cost-notes"
          />
        </label>
        <div className="mt-6 flex items-center gap-4">
          <button
            onClick={saveSettings}
            disabled={saving}
            data-testid="cost-save-btn"
            className="bg-charcoal text-base px-5 py-3 rounded-full font-semibold inline-flex items-center gap-2 disabled:opacity-60"
          >
            <Save size={14}/> {saving ? 'Saving…' : 'Save'}
          </button>
          {savedAt && <div className="text-sm text-forest">Saved at {savedAt}</div>}
        </div>
      </div>
    </div>
  );
}
